import * as Yup from 'yup'
import clsx from 'clsx'
import {Link, useNavigate} from 'react-router-dom'
import {useFormik} from 'formik'
import {useSignInMutation} from '../Auth.api'
import {useIntl} from 'react-intl'
import './signin.css'
import { ISignInResponseDto } from '../Auth.model'
import { useGetUserByIdMutation } from '../../profile/Profile.api'
import jwt_decode from 'jwt-decode'
import { useGetOrganizationByIdMutation } from '../../organization/Organization.api'
import { useRef, useState } from 'react'

export function SignIn() {
  const intl = useIntl()
  const navigate = useNavigate()

  const didSubmit = useRef(false)
  const [error, setError] = useState<string | undefined>(undefined)
  const [signIn] = useSignInMutation()
  const [getUser] = useGetUserByIdMutation()  
  const [getOrganizationById] = useGetOrganizationByIdMutation() 
 

  const isChallenged = (auth: ISignInResponseDto) => {
    if (auth.session) {
      if (auth.challengeName === 'NEW_PASSWORD_REQUIRED') {
        navigate('/auth/password-setup')
      }
    } else {
      var decoded: any = jwt_decode(auth.tokens.idToken)
      getUser(decoded['custom:userId'])      
      getOrganizationById(decoded['custom:organizationId']).unwrap().then(payload => payload)
    }
  }

  const signInSchema = Yup.object().shape({
    organization: Yup.string()
      .min(3, intl.formatMessage({id: 'INPUT.VALIDATION.MIN_LENGTH'}, {min: 3}))
      .max(255, intl.formatMessage({id: 'INPUT.VALIDATION.MAX_LENGTH'}, {max: 255}))
      .required(intl.formatMessage({id: 'INPUT.VALIDATION.REQUIRED'})),
    email: Yup.string()
      .min(3, intl.formatMessage({id: 'INPUT.VALIDATION.MIN_LENGTH'}, {min: 3}))
      .max(50, intl.formatMessage({id: 'INPUT.VALIDATION.MAX_LENGTH'}, {max: 50}))
      .required(intl.formatMessage({id: 'INPUT.VALIDATION.REQUIRED'})),
    password: Yup.string()
      .min(6, intl.formatMessage({id: 'INPUT.VALIDATION.MIN_LENGTH'}, {min: 6}))
      .max(50, intl.formatMessage({id: 'INPUT.VALIDATION.MAX_LENGTH'}, {max: 50}))
      .required(intl.formatMessage({id: 'INPUT.VALIDATION.REQUIRED'})),
  })

  const formik = useFormik({
    initialValues: {
      organization: '',
      email: '',
      password: '',
    },
    validationSchema: signInSchema,
    onSubmit: async (values, {setStatus, setSubmitting}) => {
      if (!didSubmit.current) {
        try {
          setSubmitting(true)
          const response: any = await signIn({
            organization: values.organization,
            email: values.email,
            password: values.password,
          })

          if (response && response.error) {
            const {error} = response
            switch (error.status) {
              case 401:
                setError(intl.formatMessage({id: 'ERROR.MESSAGE.WRONG_CREDENTIALS'}))
                break
              case 500:
                setError(intl.formatMessage({id: 'ERROR.MESSAGE.SERVICE_UNAVAILABLE'}))
                break
              default:
                setError(intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'}))
            }
          } else {
            isChallenged(response.data)
          }
        } catch (e: any) {
          setError(intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'}))
        } finally {
          setSubmitting(false)
        }
      }
    },
  })

  return (
    <div className='d-flex flex-column flex-row-auto'>
      <div className='d-flex flex-column-fluid flex-start'>
        <h1 className='text-white text-left mb-2'>
          {intl.formatMessage({id: 'AUTH.SIGN_IN.TITLE'})}
        </h1>
      </div>
      <div className='d-flex flex-column-fluid flex-left'>
        <div className='text-white text-left mb-5'>
          {intl.formatMessage({id: 'AUTH.SIGN_IN.DESCRIPTION'})}
        </div>
      </div>

      <form className='form w-100' onSubmit={formik.handleSubmit} noValidate>
        <div className='d-flex flex-column-fluid flex-center'>
          <div className='d-flex flex-column flex-row-fluid'>
            {error && (
              <div className='d-flex flex-column-auto flex-center'>
                <div className='alert alert-danger'>
                  <div className='alert-text font-weight-bold'>{error}</div>
                </div>
              </div>
            )}

            <div className='d-flex flex-column-auto flex-center'>
              <div className='form fv-row mb-5'>
                <i className='text-white form-control-icon fa fa-building icon font-weight-bold'></i>
                <input
                  placeholder={intl.formatMessage({id: 'ORGANIZATION'})}
                  {...formik.getFieldProps('organization')}
                  className={clsx(
                    'placeholder-gray-400 form-control form-control-auth bg-transparent',
                    {
                      'is-invalid': formik.touched.organization && formik.errors.organization,
                    },
                    {
                      'is-valid': formik.touched.organization && !formik.errors.organization,
                    }
                  )}
                  disabled={formik.isSubmitting}
                  type='text'
                  name='organization'
                  autoComplete='off'
                />
                {formik.touched.organization && formik.errors.organization && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>
                      <span role='alert'>{formik.errors.organization}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className='d-flex flex-column-auto flex-center'>
              <div className='form fv-row mb-5'>
                <i className='text-white form-control-icon fa fa-user-circle icon font-weight-bold'></i>
                <input
                  placeholder={intl.formatMessage({id: 'EMAIL'})}
                  {...formik.getFieldProps('email')}
                  className={clsx(
                    'placeholder-gray-400 form-control form-control-auth bg-transparent',
                    {
                      'is-invalid': formik.touched.email && formik.errors.email,
                    },
                    {
                      'is-valid': formik.touched.email && !formik.errors.email,
                    }
                  )}
                  disabled={formik.isSubmitting}
                  type='text'
                  name='email'
                  autoComplete='off'
                />
                {formik.touched.email && formik.errors.email && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>
                      <span role='alert'>{formik.errors.email}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className='d-flex flex-column-auto flex-center'>
              <div className='form fv-row mb-5'>
                <i className='text-white form-control-icon fa fa-key icon font-weight-bold'></i>
                <input
                  placeholder={intl.formatMessage({id: 'PASSWORD'})}
                  {...formik.getFieldProps('password')}
                  className={clsx(
                    'placeholder-gray-400 form-control form-control-auth bg-transparent',
                    {
                      'is-invalid': formik.touched.password && formik.errors.password,
                    },
                    {
                      'is-valid': formik.touched.password && !formik.errors.password,
                    }
                  )}
                  disabled={formik.isSubmitting}
                  type='password'
                  name='password'
                  autoComplete='off'
                />
                {formik.touched.password && formik.errors.password && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>
                      <span role='alert'>{formik.errors.password}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className='d-flex flex-row mt-2'>
          <div className='d-flex flex-column flex-row-fluid'>
            <button
              type='submit'
              className='btn btn-sm btn-light'
              disabled={formik.isSubmitting || !formik.isValid}
            >
              {!formik.isSubmitting && (
                <span className='indicator-label'>
                  {intl.formatMessage({id: 'LOGIN'}).toUpperCase()}
                </span>
              )}
              {formik.isSubmitting && (
                <span className='indicator-progress' style={{display: 'block'}}>
                  {intl.formatMessage({id: 'UTIL.PROCESSING'})}
                  <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                </span>
              )}
            </button>
          </div>
        </div>
      </form>

      <div className='d-flex flex-row flex-start mt-2'>
        <span className='text-white'>{intl.formatMessage({id: 'AUTH.SIGN_IN.LOGIN_ISSUES'})}</span>{' '}
        <Link to='/auth/forgot-password' className='link-primary text-white fw-semibold ms-2'>
          <u>{intl.formatMessage({id: 'AUTH.SIGN_IN.FORGOT_LINK'})}</u>
        </Link>
      </div>

      <div className='d-flex flex-row flex-start mt-2'>
        <Link to='/auth/signup' className='link-primary text-white fw-semibold '>
          <u>{intl.formatMessage({id: 'AUTH.SIGN_UP.SIGNUP_CONFIRMATION_LINK'})}</u>
        </Link>{' '}
        <span className='text-white ms-2'>{intl.formatMessage({id: 'AUTH.SIGN_UP.MESSAGE'})}</span>{' '}
      </div>
    </div>
  )
}
